import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Users, MagnifyingGlass, Star, CaretUp, CaretDown, X as XIcon, PencilSimple } from "@phosphor-icons/react";
import { CONTACT_ROLES, CONTACT_POSTURES, ROLE_LABEL, POSTURE_LABEL, POSTURE_TONE, fmtRelative } from "@/components/ContactsPanel";
import { ContactEditDialog } from "@/components/ContactEditDialog";
import { ClientEditDialog } from "@/components/ClientEditDialog";
import { CLIENT_TIERS } from "@/lib/enumLabels";

// Iter 85.7 · Directory CSV Export — turn a row array into a downloadable
// CSV blob. Wraps every field in quotes and escapes embedded quotes so
// a comma inside a title or a stray quote in an email doesn't collapse
// the column count. Uses the same header as the CSV import expects so
// a partner can round-trip a directory slice through Excel and re-upload.
const CSV_HEADERS = ["name", "title", "email", "phone", "role", "tags", "client_name", "client_tier", "client_industry", "posture", "influence", "last_contacted_at", "days_since", "decayed"];
function toCsv(rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [CSV_HEADERS.join(",")];
  for (const r of rows) {
    const row = { ...r, tags: Array.isArray(r.tags) ? r.tags.join("; ") : "" };
    lines.push(CSV_HEADERS.map((h) => esc(row[h])).join(","));
  }
  return lines.join("\n");
}

// Iter 85.99 · CRM Search — client-tier filter chip.
// Contact-recency window presets used across the CRM. `null` means "no
// filter"; any other value maps directly to the backend params.
const RECENCY_PRESETS = [
  { v: "any", label: "Any recency" },
  { v: "within_7", label: "Touched in last 7 days" },
  { v: "within_30", label: "Touched in last 30 days" },
  { v: "within_90", label: "Touched in last 90 days" },
  { v: "stale_30", label: "Stale > 30 days" },
  { v: "stale_90", label: "Stale > 90 days" },
  { v: "never", label: "Never contacted" },
];

const DEFAULT_SORT_DIR = {
  name: "asc",
  client_name: "asc",
  role: "asc",
  influence: "desc",
  last_contacted: "desc",
  days_since: "desc",
};

/**
 * ContactDirectory — Iter 85.99 · firm-wide CRM search.
 *
 * Every filter is server-side so the directory scales past a single
 * browser page. `q` fires a debounced GET /contacts request that
 * substring-matches across name / title / email / phone / LinkedIn /
 * partner-only notes / client name. Structured facets (role, posture,
 * influence, client tier, client industry, has-email/phone/LinkedIn,
 * primary, recency window) narrow the pool further. Rows deep-link to
 * the client's Contacts tab with the row auto-open behavior from Iter
 * 85.5 (?openContact=<id>).
 */
export default function ContactDirectory() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  // Filters — every one is a server-side param on `/contacts`.
  const [q, setQ] = useState("");
  const [posture, setPosture] = useState("all");
  const [influence, setInfluence] = useState("all");
  const [role, setRole] = useState("all");
  const [clientTier, setClientTier] = useState("all");
  const [clientIndustry, setClientIndustry] = useState("all");
  const [presence, setPresence] = useState("any"); // any | has_email | has_phone | has_linkedin
  const [primary, setPrimary] = useState("any"); // any | yes | no
  const [recency, setRecency] = useState("any");
  const [decayOnly, setDecayOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [sort, setSort] = useState("");
  const [sortDir, setSortDir] = useState("");
  // Iter 85.101 · Tag filter — server accepts multiple `tag=` params
  // and matches "any of", but the first-pass UI is a single-select.
  const [tag, setTag] = useState("all");
  const [allTags, setAllTags] = useState([]);
  // Iter 85.100 · Inline edit — the pencil on any row opens a dialog
  // that PATCHes /clients/{cid}/contacts/{id}. Nothing is saved until
  // the partner presses Save so a mid-edit cancel leaves the row
  // untouched.
  const [editRow, setEditRow] = useState(null);
  const applyEditedRow = (patched) => {
    setRows((prev) => prev.map((r) => (
      r.id === patched.id && r.client_id === patched.client_id ? { ...r, ...patched } : r
    )));
  };
  // Iter 86.5 · Inline PARTNER-ORG edit — a second pencil sits next to
  // the client_name column. Same dialog UX as the contact editor, but
  // PUTs /clients/{id} and cascades the update across every row in the
  // directory that carries this client_id so the list re-renders in
  // place (no page navigate, no re-fetch).
  const [editClient, setEditClient] = useState(null);
  const applyEditedClient = (saved) => {
    if (!saved?.id) return;
    setRows((prev) => prev.map((r) => (
      r.client_id === saved.id
        ? { ...r, client_name: saved.name ?? r.client_name, client_tier: saved.tier ?? r.client_tier, client_industry: saved.industry ?? r.client_industry }
        : r
    )));
  };

  // Debounce the free-text query so a fast typist doesn't fire a
  // request per keystroke. Structured facets change less often so they
  // can hit the wire immediately.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 220);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedQ) params.set("q", debouncedQ);
    if (posture !== "all") params.set("posture", posture);
    if (influence !== "all") params.set("influence", influence);
    if (role !== "all") params.set("role", role);
    if (clientTier !== "all") params.set("client_tier", clientTier);
    if (clientIndustry !== "all") params.set("client_industry", clientIndustry);
    if (presence === "has_email") params.set("has_email", "true");
    if (presence === "has_phone") params.set("has_phone", "true");
    if (presence === "has_linkedin") params.set("has_linkedin", "true");
    if (primary === "yes") params.set("is_primary", "true");
    if (primary === "no") params.set("is_primary", "false");
    if (recency.startsWith("within_")) params.set("contacted_within_days", recency.slice(7));
    if (recency.startsWith("stale_")) params.set("not_contacted_within_days", recency.slice(6));
    if (recency === "never") params.set("not_contacted_within_days", "999999");
    if (decayOnly) params.set("decay_only", "true");
    if (activeOnly) params.set("active_only", "true");
    if (tag !== "all") params.append("tag", tag);
    if (sort) params.set("sort", sort);
    if (sortDir) params.set("sort_dir", sortDir);
    api.get(`/contacts?${params.toString()}`).then((r) => {
      setRows(r.data.contacts || []);
      setTotal(r.data.total || 0);
      setCounts(r.data.counts || {});
      if (Array.isArray(r.data.all_tags)) setAllTags(r.data.all_tags);
    }).finally(() => setLoading(false));
  }, [debouncedQ, posture, influence, role, clientTier, clientIndustry, presence, primary, recency, decayOnly, activeOnly, tag, sort, sortDir]);

  // Industries are a free-form field on Client, so the list of choices
  // must come from what actually exists rather than a hardcoded enum.
  // Fetching once on mount is cheap and keeps the picker in sync with
  // whatever partners have entered.
  const [industryOptions, setIndustryOptions] = useState([]);
  useEffect(() => {
    api.get(`/clients`).then((r) => {
      const seen = new Set();
      for (const c of r.data || []) {
        const v = (c.industry || "").trim();
        if (v && !seen.has(v.toLowerCase())) seen.add(v.toLowerCase());
      }
      setIndustryOptions(Array.from(seen).sort());
    }).catch(() => setIndustryOptions([]));
  }, []);

  const setSortCol = (col) => {
    if (sort !== col) {
      setSort(col);
      setSortDir(DEFAULT_SORT_DIR[col] || "asc");
      return;
    }
    // Second click flips direction; third click clears the sort so the
    // default "needs attention" order returns.
    if (sortDir === DEFAULT_SORT_DIR[col]) {
      setSortDir(DEFAULT_SORT_DIR[col] === "asc" ? "desc" : "asc");
    } else {
      setSort("");
      setSortDir("");
    }
  };

  const resetAll = () => {
    setQ(""); setPosture("all"); setInfluence("all"); setRole("all");
    setClientTier("all"); setClientIndustry("all"); setPresence("any");
    setPrimary("any"); setRecency("any"); setDecayOnly(false);
    setActiveOnly(true); setTag("all"); setSort(""); setSortDir("");
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (debouncedQ) n++;
    if (posture !== "all") n++;
    if (influence !== "all") n++;
    if (role !== "all") n++;
    if (clientTier !== "all") n++;
    if (clientIndustry !== "all") n++;
    if (presence !== "any") n++;
    if (primary !== "any") n++;
    if (recency !== "any") n++;
    if (decayOnly) n++;
    if (!activeOnly) n++;
    if (tag !== "all") n++;
    return n;
  }, [debouncedQ, posture, influence, role, clientTier, clientIndustry, presence, primary, recency, decayOnly, activeOnly, tag]);

  return (
    <PageShell size="content" testid="contact-directory">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
          <Users size={11} weight="fill" /> People book
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-1">Contact directory</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Every person around the firm · {total} contact{total === 1 ? "" : "s"}
          {typeof counts.decayed === "number" && counts.decayed > 0 && (
            <> · <span className="text-amber-600 dark:text-amber-400">{counts.decayed} need a touch</span></>
          )}
          {typeof counts.high_influence === "number" && (
            <> · {counts.high_influence} high-influence</>
          )}
        </p>
      </div>

      {/* Search + primary filters — always visible so the intent is
          obvious the moment the page loads. */}
      <div className="border border-border bg-card p-4 space-y-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, title, email, phone, LinkedIn, notes, partner org…"
              className="rounded-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-8 text-sm"
              data-testid="contact-directory-search"
            />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-role">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {CONTACT_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={posture} onValueChange={setPosture}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-posture">
              <SelectValue placeholder="Posture" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All postures</SelectItem>
              {CONTACT_POSTURES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={influence} onValueChange={setInfluence}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-influence">
              <SelectValue placeholder="Influence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All influence</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={clientTier} onValueChange={setClientTier}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-tier">
              <SelectValue placeholder="Partner tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All partner tiers</SelectItem>
              {CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={clientIndustry} onValueChange={setClientIndustry}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-44 text-xs" data-testid="contact-directory-industry">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {industryOptions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={presence} onValueChange={setPresence}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-presence">
              <SelectValue placeholder="Has…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any details</SelectItem>
              <SelectItem value="has_email">Has email</SelectItem>
              <SelectItem value="has_phone">Has phone</SelectItem>
              <SelectItem value="has_linkedin">Has LinkedIn</SelectItem>
            </SelectContent>
          </Select>
          <Select value={primary} onValueChange={setPrimary}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-primary">
              <SelectValue placeholder="Primary POC" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="yes">Primary only</SelectItem>
              <SelectItem value="no">Non-primary</SelectItem>
            </SelectContent>
          </Select>
          <Select value={recency} onValueChange={setRecency}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-52 text-xs" data-testid="contact-directory-recency">
              <SelectValue placeholder="Recency" />
            </SelectTrigger>
            <SelectContent>
              {RECENCY_PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-40 text-xs" data-testid="contact-directory-tag">
              <SelectValue placeholder="Tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="contact-directory-decay-toggle-label">
            <input
              type="checkbox"
              checked={decayOnly}
              onChange={(e) => setDecayOnly(e.target.checked)}
              className="rounded-none"
              data-testid="contact-directory-decay-toggle"
            />
            <span>Decay only</span>
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="rounded-none"
              data-testid="contact-directory-active-toggle"
            />
            <span>Active partners</span>
          </label>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={resetAll}
              data-testid="contact-directory-reset"
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
            >
              <XIcon size={11} weight="bold" /> Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              const csv = toCsv(rows);
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const ts = new Date().toISOString().slice(0, 10);
              a.download = `contacts-${ts}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors ml-auto"
            data-testid="contact-directory-export-csv"
            disabled={rows.length === 0}
          >
            Export CSV · {rows.length}
          </button>
        </div>
      </div>

      <div className="border border-border bg-card">
        {/* Sortable column header row. Clicking a column cycles: unsorted →
            default direction → opposite direction → unsorted. */}
        <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-border bg-secondary/30 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
          <SortHeader col="name" label="Name" span={4} sort={sort} sortDir={sortDir} onClick={setSortCol} />
          <SortHeader col="client_name" label="Partner org" span={3} sort={sort} sortDir={sortDir} onClick={setSortCol} />
          <SortHeader col="influence" label="Posture · Influence" span={2} sort={sort} sortDir={sortDir} onClick={setSortCol} />
          <SortHeader col="last_contacted" label="Last touch · Owner" span={3} sort={sort} sortDir={sortDir} onClick={setSortCol} align="right" />
        </div>
        {loading && (
          <div className="px-5 py-16 text-center text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-5 py-16 text-center text-xs text-muted-foreground" data-testid="contact-directory-empty">
            No contacts match those filters.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-border" data-testid="contact-directory-list">
            {rows.map((r) => (
              <li key={`${r.client_id}-${r.id}`} className="px-5 py-3 hover:bg-secondary/30 relative group" data-testid={`directory-row-${r.id}`}>
                <Link to={`/clients/${r.client_id}?tab=stakeholders&openContact=${r.id}`} className="grid grid-cols-12 gap-3 items-center pr-8">
                  <div className="col-span-4">
                    <div className="flex items-center gap-2">
                      {r.is_primary && <Star size={11} weight="fill" className="text-primary shrink-0" />}
                      <span className="font-display font-bold text-sm truncate">{r.name}</span>
                    </div>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate mt-0.5">
                      {r.title || ROLE_LABEL[r.role]}
                    </div>
                  </div>
                  <div className="col-span-3 text-xs text-muted-foreground truncate">
                    <div className="flex items-center gap-1 group/client">
                      <span className="truncate">{r.client_name}</span>
                      {/* Iter 86.5 · Inline partner-org edit pencil.
                          Stops link navigation so the user stays on
                          the directory and lands in the client
                          editor. Fades in on row hover; keyboard-
                          focus keeps it visible for a11y. */}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditClient({ id: r.client_id, name: r.client_name, tier: r.client_tier, industry: r.client_industry }); }}
                        data-testid={`directory-client-edit-${r.client_id}`}
                        title={`Edit partner org · ${r.client_name}`}
                        className="shrink-0 h-4 w-4 inline-flex items-center justify-center text-muted-foreground/40 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-primary transition-opacity"
                      >
                        <PencilSimple size={10} weight="bold" />
                      </button>
                    </div>
                    {(r.client_tier || r.client_industry) && (
                      <div className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground/70 truncate mt-0.5">
                        {[r.client_tier, r.client_industry].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {Array.isArray(r.tags) && r.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`directory-tags-${r.id}`}>
                        {r.tags.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="text-[0.5625rem] font-mono uppercase tracking-widest border border-brass/50 bg-brass-soft/30 text-brass px-1 py-0.5"
                          >
                            {t}
                          </span>
                        ))}
                        {r.tags.length > 4 && (
                          <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground/70">
                            +{r.tags.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center gap-1.5">
                    <span className={`text-[0.5625rem] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${POSTURE_TONE[r.posture] || POSTURE_TONE.unknown}`}>
                      {POSTURE_LABEL[r.posture] || POSTURE_LABEL.unknown}
                    </span>
                    <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">{r.influence}</span>
                  </div>
                  <div className="col-span-3 text-right text-[0.625rem] font-mono uppercase tracking-widest">
                    {r.decayed ? (
                      <span className="text-amber-600 dark:text-amber-400" data-testid={`directory-decay-${r.id}`}>Reach out</span>
                    ) : r.last_contacted_at ? (
                      <span className="text-muted-foreground">{fmtRelative(r.last_contacted_at)}</span>
                    ) : (
                      <span className="text-muted-foreground/60">never touched</span>
                    )}
                    {r.owner_name && (
                      <div className="text-muted-foreground/70 mt-0.5">owner · {r.owner_name}</div>
                    )}
                  </div>
                </Link>
                {/* Iter 85.100 · Inline edit trigger. Absolutely positioned
                    so it sits on top of the row without disturbing the
                    grid; fades in on hover so it doesn't compete visually
                    with the row content, but stays keyboard-focusable
                    (focus-visible ring) so it's not mouse-only. */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditRow(r); }}
                  data-testid={`directory-edit-${r.id}`}
                  title="Edit contact"
                  className="absolute right-3 top-3 h-7 w-7 inline-flex items-center justify-center border border-transparent text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-primary hover:border-primary/60 transition-opacity"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ContactEditDialog
        row={editRow}
        allTags={allTags}
        onOpenChange={(o) => { if (!o) setEditRow(null); }}
        onSaved={applyEditedRow}
      />
      <ClientEditDialog
        row={editClient}
        industryOptions={industryOptions}
        onOpenChange={(o) => { if (!o) setEditClient(null); }}
        onSaved={applyEditedClient}
      />
    </PageShell>
  );
}

function SortHeader({ col, label, span, sort, sortDir, onClick, align }) {
  const active = sort === col;
  const spanClass = { 2: "col-span-2", 3: "col-span-3", 4: "col-span-4" }[span] || "col-span-3";
  return (
    <button
      type="button"
      onClick={() => onClick(col)}
      data-testid={`contact-directory-sort-${col}`}
      className={`${spanClass} text-[0.5938rem] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""} ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
    >
      <span>{label}</span>
      {active && (sortDir === "asc" ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)}
    </button>
  );
}
